import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { useFetcher, useOutletContext, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { CommentActionType, CommentData, TPostCommentsElement } from '@shared/dream-components';
import { PostCommentsElement } from '@shared/dream-components';

import CommentAction from '~/components/CommentAction';
import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import SlideUpModal from '~/components/Modal/SlideUpModal';
import Text from '~/components/Text';
import { usePostPageContext } from '~/context/usePostPageContext';
import useAvatarUpload from '~/hooks/useAvatarUpload';
import useLikedIds from '~/hooks/useLoaders/useLikedIds';
import type { OrderType } from '~/hooks/useLoaders/usePaginatedComments';
import usePaginatedComments from '~/hooks/useLoaders/usePaginatedComments';
import useToast, { type ToastData } from '~/hooks/useToast';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Admin } from '~/interfaces/admin';
import type { CommentType } from '~/interfaces/comment';
import { CommentSectionState } from '~/interfaces/comment';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Subscription } from '~/interfaces/subscriptions';
import DeleteAction from '~/pages/post/CommentsSection/DeleteAction';
import ReportAction from '~/pages/post/CommentsSection/ReportAction';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';

const COMMENTS_ORDER_OPTIONS = [
  { label: 'Newest first', value: 'desc' },
  { label: 'Oldest first', value: 'asc' },
  { label: 'Most popular', value: 'popular' },
];

const getEditWindowMs = (settingMinutes?: number | null): number | null => {
  if (settingMinutes === null) return null; // always editable
  if (settingMinutes === 0) return 0; // never editable
  const minutes = settingMinutes ?? 60; // default 60 minutes
  return minutes * 60 * 1000;
};

const isWithinEditWindow = (createdAt: string, windowMs: number | null): boolean => {
  if (windowMs === null) return true;
  if (windowMs === 0) return false;
  return Date.now() - new Date(createdAt).getTime() < windowMs;
};

const mapComments = (
  comments: CommentData[],
  admin?: Admin,
  subscription?: Subscription,
  editWindowMs: number | null = 60 * 60 * 1000,
  isCommentLiked: (id: string) => boolean = () => false,
): CommentData[] => {
  const currentUserId = String(admin?.id ?? subscription?.id ?? '');

  return comments.map((comment) => {
    const isOwner = String(comment.commenter_id) === currentUserId;
    const canEdit = isOwner && (comment.is_moderator_comment || isWithinEditWindow(comment.created_at, editWindowMs));

    return {
      ...comment,
      sub_comments: mapComments(comment.sub_comments || [], admin, subscription, editWindowMs, isCommentLiked),
      is_liked: isCommentLiked(comment.id),
      can_delete: comment.commenter_id === subscription?.id,
      can_edit: canEdit,
    };
  });
};

const filterDeletedComments = (comments: CommentData[]): CommentData[] => {
  return comments.filter((comment) => {
    if (comment.deleted_by_commenter_at) {
      return false;
    }

    comment.sub_comments = filterDeletedComments(comment.sub_comments || []);
    return true;
  });
};

interface Props {
  node: TPostCommentsElement;
  children: ReactNode | ReactNode[];
}

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex max-w-2xl flex-col space-y-2 py-8">
    <Text as="p" type="body" size="md" className="text-center leading-tight tracking-normal">
      {message}
    </Text>
  </div>
);

const PostComments = ({ node, children }: Props) => {
  const { canShowCommentsNode } = usePostPageContext() || { canShowCommentsNode: false };

  const { t } = useTranslation();

  const { handleOpenModal } = useGlobalModals();

  const [commentActionType, setCommentActionType] = useState<CommentActionType | null>(null);
  const [actionableComment, setActionableComment] = useState<CommentData | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

  const editFetcher = useFetcher<{ toast?: ToastData }>();
  const pendingEditRef = useRef<{ commentId: string; newContent: string } | null>(null);
  const isEditSubmitting = editFetcher.state === 'submitting';
  const editFetcherData = editFetcher.data;
  useToast(editFetcherData?.toast);

  const { admin } = useOutletContext<OutletContext>();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const { post, site, onlyRenderComments } = useTypedLoaderData<PostLoaderData>();

  const postId = post?.id;

  // Use shared hook for liked IDs (single fetch, cached)
  const { isCommentLiked } = useLikedIds({ postId, enabled: canShowCommentsNode });

  // Reset edit state when navigating to a different post
  useEffect(() => {
    setEditingCommentId(null);
  }, [postId]);

  const [, setSearchParams] = useSearchParams();

  const initialCommentOrder = site?.viewable_site_version?.settings?.comments_ordering as OrderType;

  // Fetch comments for the post
  // This does not run if comments are disabled on this post or on the publication level
  // This also runs on the client so it is not page blocking to render the post. Comments are loaded in the background
  const {
    initialFetched,
    comments,
    optimisticallyRenderNewComment,
    optimisticallyDeleteTopLevelComment,
    optimisticallyDeleteSubComment,
    optimisticallyUpdateComment,
    commentOrder,
    reorderComments,
    loadMore,
    isLoadingMore,
    remainingCount,
    hasNextPage,
  } = usePaginatedComments({
    postId: post?.id || '',
    enabled: canShowCommentsNode,
    order: initialCommentOrder,
  });

  const isSubscriberLoggedIn = Boolean(subscription);
  const isLoggedInAsAdmin = Boolean(admin);
  const isNotLoggedIn = !isLoggedInAsAdmin && !isSubscriberLoggedIn;
  const subscriberAvatarUrl = subscription?.avatar_url;

  // Avatar upload hook for immediate profile picture updates
  const { uploadAvatar, isUploading: isAvatarUploading, optimisticAvatarUrl } = useAvatarUpload();

  const displayAvatarUrl = optimisticAvatarUrl || subscriberAvatarUrl;

  const hasNoComments = !comments?.length;
  const isBannedFromPostComments = Boolean(subscription?.comment_post_bans?.includes(post?.id));
  const hasActiveCommentBan = Boolean(subscription?.comment_publication_ban) || isBannedFromPostComments;
  const commentsLocked = post?.comments_state === CommentSectionState.LOCKED || hasActiveCommentBan;
  const isPremiumCommentsOnly = post?.comments_state === CommentSectionState.PREMIUM;
  const needsToSetProfileDetails = !subscription?.comment_details_set && !isLoggedInAsAdmin;
  const showActionsDropdown = !commentsLocked && !needsToSetProfileDetails;

  const renderCommentAction = !commentsLocked || isLoggedInAsAdmin;

  const commentEditWindow = site?.viewable_site_version?.settings?.comment_edit_window;
  const editWindowMs = getEditWindowMs(commentEditWindow);

  const filteredComments: CommentData[] = useMemo(() => {
    if (!initialFetched) {
      return [];
    }
    return mapComments(filterDeletedComments(comments), admin, subscription, editWindowMs, isCommentLiked);
  }, [admin, comments, subscription, initialFetched, editWindowMs, isCommentLiked]);

  const onCreate = (newComment: CommentType, parentCommentId?: string) => {
    optimisticallyRenderNewComment(newComment, parentCommentId);
  };

  const handleClickLike = async (formData: FormData) => {
    if (isNotLoggedIn) {
      handleOpenModal('login', { preventScrollReset: true });
      return false;
    }

    if (!post || !subscription) {
      toast.error('Something went wrong, please try again!');
      return false;
    }

    formData.append('post_id', post.id);
    formData.append('subscription_id', String(subscription.id));

    if (admin) {
      formData.append('user_id', String(admin.id));
    }

    if (isLoggedInAsAdmin) {
      await fetch('/admins/comment_like', {
        method: 'POST',
        body: formData,
      });
    } else {
      await fetch('/comment_like', {
        method: 'POST',
        body: formData,
      });
    }

    return true;
  };

  const handleClickAction = (action: CommentActionType, comment: CommentData) => {
    if (action === 'edit') {
      setEditingCommentId(comment.id);
      return;
    }
    setCommentActionType(action);
    setActionableComment(comment);
  };

  // Apply optimistic update after successful edit
  const editSucceeded = editFetcherData && editFetcherData.toast?.status !== 'error';
  useEffect(() => {
    if (!editSucceeded || !pendingEditRef.current) return;

    const { commentId, newContent } = pendingEditRef.current;
    optimisticallyUpdateComment(commentId, newContent);
    setEditingCommentId(null);
    pendingEditRef.current = null;
  }, [editSucceeded, optimisticallyUpdateComment]);

  const handleSaveEdit = (commentId: string, newContent: string) => {
    const formData = new FormData();
    formData.append('comment_id', commentId);
    formData.append('content', newContent);
    formData.append('post_id', post.id);
    formData.append('edit_comment_success_message', t('comments.editing.edit_comment_success'));
    formData.append('edit_comment_error_message', t('comments.editing.edit_comment_error'));
    formData.append('characters_error_message', t('comments.commenting.characters_error'));

    // Store pending edit info for the useEffect to handle
    pendingEditRef.current = { commentId, newContent };

    const formAction = isLoggedInAsAdmin ? '/admins/edit_comment' : '/edit_comment';

    if (isLoggedInAsAdmin && admin) {
      formData.append('user_id', admin.id);
    } else if (subscription) {
      formData.append('subscription_id', String(subscription.id));
    }

    editFetcher.submit(formData, { method: 'post', action: formAction });
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
  };

  const handleClickLogin = () => {
    handleOpenModal('login', { preventScrollReset: true });
  };

  const handleClickSubscribe = () => {
    handleOpenModal('signup', { preventScrollReset: true });
  };

  if (!canShowCommentsNode) {
    return null;
  }

  return (
    <>
      <SlideUpModal
        title={commentActionType === 'delete' ? 'Delete Comment' : 'Report a comment'}
        description={
          commentActionType === 'delete'
            ? 'Are you sure you want to delete the comment?'
            : 'Share with a moderator your concerns about the comment you are reporting'
        }
        isOpen={!!actionableComment}
        onClose={() => {
          setActionableComment(null);
          setCommentActionType(null);
        }}
        backgroundColor={site.publication.web_theme.background_color}
        textColor={site.publication.web_theme.text_on_background_color}
        closeButtonBackgroundColor={site.publication.web_theme.secondary_color}
        closeButtonTextColor={site.publication.web_theme.text_on_secondary_color}
      >
        <>
          {commentActionType === 'delete' && (
            <DeleteAction
              postId={post.id}
              commentId={actionableComment?.id}
              commenterId={actionableComment?.commenter_id}
              subscriptionId={subscription?.id}
              userId={admin?.id}
              isLoggedInAsAdmin={isLoggedInAsAdmin}
              buttonBackgroundColor={site.publication.web_theme.primary_color}
              buttonTextColor={site.publication.web_theme.text_on_primary_color}
              onDelete={() => {
                if (!actionableComment) {
                  return;
                }

                if (actionableComment.depth_level === 1) {
                  optimisticallyDeleteTopLevelComment(actionableComment.id);
                } else {
                  optimisticallyDeleteSubComment(actionableComment.id);
                }

                setActionableComment(null);
                setCommentActionType(null);
              }}
            />
          )}
          {commentActionType === 'report' && (
            <ReportAction
              postId={post.id}
              commentId={actionableComment?.id}
              subscriptionId={subscription?.id}
              onReport={() => {
                setActionableComment(null);
                setCommentActionType(null);
              }}
              textareaTextColor={site.publication.web_theme.primary_color}
              buttonBackgroundColor={site.publication.web_theme.primary_color}
              buttonTextColor={site.publication.web_theme.text_on_primary_color}
            />
          )}
        </>
      </SlideUpModal>
      <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
        <CommentAction
          isPremiumCommentsOnly={isPremiumCommentsOnly}
          onCommentCreated={onCreate}
          isNotLoggedIn={isNotLoggedIn}
        >
          {(isSubmitting, setCurrentComment, replyingToCommentId, setReplyingToCommentId) => (
            <PostCommentsElement
              id="comments"
              isLive
              isSubmitting={isSubmitting}
              element={{
                ...node,
                attrs: {
                  ...node.attrs,
                  data: {
                    comments: filteredComments,
                  },
                },
              }}
              onReorderComments={(newOrder: string) => reorderComments(newOrder as OrderType)}
              commentsOrderOptions={COMMENTS_ORDER_OPTIONS}
              commentOrder={commentOrder || initialCommentOrder}
              showBackToPost={onlyRenderComments}
              onBackToPost={() => setSearchParams({})}
              postTitle={post.web_title}
              onLoadMoreComments={loadMore}
              isLoadingMoreComments={isLoadingMore}
              showLoadMore={initialFetched && hasNextPage && !hasNoComments}
              remainingCommentsCount={remainingCount}
              canComment={renderCommentAction}
              areCommentsLoading={!initialFetched}
              showActions={showActionsDropdown || isLoggedInAsAdmin}
              onClickAction={handleClickAction}
              onClickLike={handleClickLike}
              onClickLogin={handleClickLogin}
              onClickSubscribe={handleClickSubscribe}
              setCurrentComment={setCurrentComment}
              replyingToCommentId={replyingToCommentId}
              setReplyingToCommentId={setReplyingToCommentId}
              isVisitorLoggedIn={!isNotLoggedIn}
              isLoggedInAsAdmin={isLoggedInAsAdmin}
              commenterId={subscription?.id}
              editingCommentId={editingCommentId}
              onEditComment={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              isEditSubmitting={isEditSubmitting}
              translations={{
                'comments.count.singular': t('comments.count.singular', {
                  count: 1,
                }),
                'comments.count.plural': t('comments.count.plural', {
                  count: filteredComments.length || 0,
                }),
                'shared.cta.cancel': t('shared.cta.cancel'),
                'shared.cta.login': t('shared.cta.login'),
                'shared.cta.subscribe': t('shared.cta.subscribe'),
                'shared.cta.or': t('shared.cta.or'),
                'shared.cta.to_participate': t('shared.cta.to_participate'),
                'comments.like': t('comments.like'),
                'comments.replying.reply': t('comments.replying.reply'),
                'comments.editing.edit': t('comments.editing.edit'),
                'comments.editing.save': t('comments.editing.save'),
                'comments.editing.saving': t('comments.editing.saving'),
                'comments.editing.edited': t('comments.editing.edited'),
                'author.author': t('author.author'),
              }}
              avatarUrl={displayAvatarUrl}
              onAvatarUpload={uploadAvatar}
              isAvatarUploading={isAvatarUploading}
              canUploadAvatar={isSubscriberLoggedIn && !isLoggedInAsAdmin}
            >
              {children}
            </PostCommentsElement>
          )}
        </CommentAction>
      </ErrorBoundary>
    </>
  );
};

export default PostComments;
